<template>
  <div>
    <v-list-item class="px-3 py-1">
      <div class="text-caption text-medium-emphasis mb-1" style="opacity: 0.8">
        Content Language
      </div>
      <v-radio-group
        :model-value="contentLanguage"
        :disabled="saving"
        inline
        hide-details
        density="compact"
        data-test="content-language-radio-group"
        @update:modelValue="onChangeContentLanguage"
      >
        <v-radio value="en" color="primary" data-test="content-language-option-en">
          <template #label>
            <span class="text-caption text-medium-emphasis">English</span>
          </template>
        </v-radio>
        <v-radio value="es" color="primary" class="ml-2" data-test="content-language-option-es">
          <template #label>
            <span class="text-caption text-medium-emphasis">Spanish</span>
          </template>
        </v-radio>
      </v-radio-group>
    </v-list-item>

    <div
      class="px-3 pb-2 text-caption text-medium-emphasis text-wrap content-language-help"
      style="opacity: 0.8"
    >
      Applies to newly generated content. Existing eligible inventory is reconciled during scheduled
      content-generation runs.
    </div>

    <v-dialog
      v-model="showConfirmation"
      :persistent="saving"
      max-width="560"
      data-test="content-language-confirmation-dialog"
    >
      <v-card rounded="lg">
        <v-card-title class="text-subtitle-1">
          Change Content Language to {{ pendingLanguageLabel }}?
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="text-body-2 mb-3">
            New content will be generated in {{ pendingLanguageLabel }}. Existing eligible inventory
            will be updated during a scheduled content-generation run, which runs every 2 hours.
          </p>
          <p class="text-body-2 mb-0">
            To update existing inventory immediately, use Apply Content Language from the Actions
            menu.
          </p>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="saving"
            data-test="content-language-change-cancel"
            @click="cancelChange"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="saving"
            data-test="content-language-change-confirm"
            @click="confirmChange"
          >
            Change to {{ pendingLanguageLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
/**
 * The account's content-language choice.
 *
 * Owns one canonical control and its save. Description is the source of truth
 * when reading; both Description and Highlight are written explicitly on save.
 * Reporting is left to the parent, which already owns the notification surface.
 */
import { computed, ref, watch } from 'vue'

import { getLanguageLabel } from '../contentLanguage'
import type { ResolvedContentLanguage } from '../contentLanguage'
import {
  buildContentLanguagePatch,
  DEFAULT_DESCRIPTION_LANGUAGE,
  DESCRIPTION_LANGUAGE_KEY,
} from '../productSettings'
import { useProductSettings } from '../useProductSettings'

const emit = defineEmits<{
  saved: [message: string]
  error: [message: string]
}>()

const { languages, save } = useProductSettings()

const contentLanguage = ref<ResolvedContentLanguage>(DEFAULT_DESCRIPTION_LANGUAGE)
const pendingLanguage = ref<ResolvedContentLanguage | null>(null)
const showConfirmation = ref(false)
const saving = ref(false)

const pendingLanguageLabel = computed(() =>
  getLanguageLabel(pendingLanguage.value ?? contentLanguage.value),
)

// Description is canonical. Highlight is ignored so legacy mismatches and
// same-as-description never change what the form shows.
watch(
  () => languages.value.descriptionLanguage,
  (descriptionLanguage) => {
    contentLanguage.value = descriptionLanguage
    pendingLanguage.value = null
    showConfirmation.value = false
  },
  { immediate: true },
)

const cancelChange = () => {
  pendingLanguage.value = null
  showConfirmation.value = false
}

const confirmChange = async () => {
  if (pendingLanguage.value === null) {
    showConfirmation.value = false
    return
  }

  const language = pendingLanguage.value
  const previousLanguage = contentLanguage.value
  const patch = buildContentLanguagePatch(language)
  saving.value = true

  try {
    if (!(await save(patch))) throw new Error('Failed to save content language')

    contentLanguage.value = language
    pendingLanguage.value = null
    showConfirmation.value = false
    emit('saved', `Content Language changed to ${getLanguageLabel(language)}.`)
  } catch (e) {
    console.error(e)
    contentLanguage.value = previousLanguage
    emit('error', 'Failed to save content language')
  } finally {
    saving.value = false
  }
}

const onChangeContentLanguage = (value: unknown) => {
  const patch = buildContentLanguagePatch(value)
  const nextLanguage = patch[DESCRIPTION_LANGUAGE_KEY] as ResolvedContentLanguage

  if (nextLanguage === contentLanguage.value) return

  pendingLanguage.value = nextLanguage
  showConfirmation.value = true
}
</script>

<style scoped>
/* Wrap within the menu width set by sibling controls; do not expand the menu. */
.content-language-help {
  width: 0;
  min-width: 100%;
  overflow-wrap: break-word;
}
</style>
